import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { useIsCanvasCentricApp } from "@/context/CanvasCentricContext";
import { UploadTarget, useFileRecordUpload } from "@/hooks/useFileRecordUpload";
import { cn } from "@/utils";
import {
  Camera,
  Check,
  ChevronRight,
  ImageIcon,
  Loader2,
  MessageSquare,
  Mic,
  Upload,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { setFullAppOverride } from "./fullAppOverride";
import { MobileScreen } from "./MobileScreen";
import { UploadTargetSheet } from "./UploadTargetSheet";

/**
 * Native mobile home — a focused first screen that leads with the two primary
 * use cases: **file upload** (to a workspace or canvas) and **voice**. Everything
 * else in the app stays reachable via the drawer or the "Open full app" hatch.
 */
export function MobileHomePage() {
  const navigate = useNavigate();
  const { isCanvasCentricApp } = useIsCanvasCentricApp();
  const { data: userInfo } = useUserInfo();
  const { enqueueFiles, uploadedFiles } = useFileRecordUpload();

  // "Recent chats" opens the chats list on the legacy shell; canvas-centric has
  // no dedicated list route (recents live on the home), so it drops into the
  // full app home instead.
  const openRecentChats = () => {
    if (isCanvasCentricApp) {
      setFullAppOverride(true);
      navigate(ROUTES.HOME);
    } else {
      navigate(ROUTES.RECENTS);
    }
  };

  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const filesInputRef = useRef<HTMLInputElement>(null);
  const photosInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const onPicked = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (!files.length) return;
    setPendingFiles(files);
    setSheetOpen(true);
  };

  const onSelectTarget = async (target: UploadTarget) => {
    const files = pendingFiles;
    setPendingFiles(null);
    if (!files?.length) return;
    try {
      await enqueueFiles(files, target);
      toast.success(
        `Uploading ${files.length} file${files.length > 1 ? "s" : ""}…`,
      );
    } catch {
      toast.error("Couldn't start the upload.");
    }
  };

  const firstName = userInfo?.user?.first_name;

  return (
    <MobileScreen>
      <div className="flex min-h-0 w-full flex-1 flex-col gap-5 py-3 pb-safe">
        <div>
          <h1 className="text-2xl font-semibold">
            {firstName ? `Hi, ${firstName}` : "Cohesive"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Upload files or dictate to get started.
          </p>
        </div>

        {/* Two primary actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => filesInputRef.current?.click()}
            className="flex aspect-square flex-col items-start justify-between rounded-2xl bg-rust p-4 text-left text-white active:scale-[0.98]"
          >
            <Upload className="h-7 w-7" />
            <span className="text-lg font-semibold">Upload files</span>
          </button>
          <button
            type="button"
            onClick={() => navigate(ROUTES.MOBILE.VOICE)}
            className="flex aspect-square flex-col items-start justify-between rounded-2xl border bg-card p-4 text-left active:scale-[0.98]"
          >
            <Mic className="h-7 w-7 text-rust" />
            <span className="text-lg font-semibold">Voice</span>
          </button>
        </div>

        {/* Image-specific sources */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => photosInputRef.current?.click()}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border py-3 text-sm font-medium active:bg-accent"
          >
            <ImageIcon className="h-4 w-4" /> Photos
          </button>
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border py-3 text-sm font-medium active:bg-accent"
          >
            <Camera className="h-4 w-4" /> Camera
          </button>
        </div>

        {/* Upload queue status */}
        {uploadedFiles.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Uploads
            </p>
            <ul className="flex flex-col gap-1">
              {uploadedFiles.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                >
                  <UploadStatusIcon status={file.status} />
                  <span className="flex-1 truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {uploadStatusLabel(file.status)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer links */}
        <div className="mt-auto flex flex-col gap-1 pt-2">
          <FooterLink
            icon={<MessageSquare className="h-4 w-4" />}
            label="Recent chats"
            onClick={openRecentChats}
          />
          <FooterLink
            icon={<ChevronRight className="h-4 w-4" />}
            label="Open full app"
            onClick={() => {
              setFullAppOverride(true);
              navigate(ROUTES.HOME);
            }}
          />
        </div>

        {/* Hidden pickers — WKWebView renders the native picker/camera for free */}
        <input
          ref={filesInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => onPicked(e.target.files)}
        />
        <input
          ref={photosInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => onPicked(e.target.files)}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => onPicked(e.target.files)}
        />

        <UploadTargetSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          onSelect={onSelectTarget}
          fileCount={pendingFiles?.length}
        />
      </div>
    </MobileScreen>
  );
}

function UploadStatusIcon({ status }: { status: string }) {
  if (status === "failed") return <X className="h-4 w-4 text-lc-blocked" />;
  if (status === "processed")
    return <Check className="h-4 w-4 text-lc-active" />;
  return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
}

function uploadStatusLabel(status: string): string {
  switch (status) {
    case "failed":
      return "Failed";
    case "processed":
      return "Done";
    case "processing":
      return "Processing";
    default:
      return "Uploading";
  }
}

function FooterLink({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center justify-between rounded-lg px-3 py-3 text-sm font-medium active:bg-accent",
      )}
    >
      <span className="flex items-center gap-2">{label}</span>
      <span className="text-muted-foreground">{icon}</span>
    </button>
  );
}

export default MobileHomePage;
